import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import flow from 'lodash/flow';
import includes from 'lodash/includes';

// decorators

// actions
import { setBranchShowingStatus, updateBranchLink } from '../actions/app';
import { openNowPlayingDialog } from '../actions/dialog';
import { logMobileWebActivity } from '../actions/logging';
import mobileWeb from '../constants/analytics/categoryActionLabel/mobileWeb';

// utils
import didRouteChange from '../utils/didRouteChange';
import {
  isBranchInitialized,
  setBranchViewData,
  createBranchLink,
  addBranchListener,
  closeBranchJourney,
  reopenBranchJourney,
  isBlacklistedBranchRoute,
} from '../utils/branch/branchHelper';
import { gotieTrackClick } from '../../client/utils/gotieTracking';
import gtagTrack from '../utils/gtagTrack';
import { isProfile } from '../utils/guideItemTypes';
import isServer from '../utils/isServer';

// constants
import { playCampaign } from '../constants/analytics/branch';
import { PLAY_OPEN_IN_APP_ENABLED } from '../constants/experiments/redirect';
import {
  MW_LANDING_DIRECT_TO_NOW_PLAYING,
  MW_LANDING_DISABLED,
  MW_LANDING_DISABLED_GUIDEIDS,
  MW_LANDING_LANDINGONLY_DISABLED,
} from '../constants/experiments/mobileLanding';
import { selectIsMobile, selectTuneInUserSerial } from '../selectors/app';
import connectWithExperiments from './connectWithExperiments';

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        openNowPlayingDialog,
        setBranchShowingStatus,
        updateBranchLink,
        logMobileWebActivity,
      },
      dispatch,
    ),
  };
}

export function mapStateToProps(state) {
  return {
    isMobile: selectIsMobile(state),
    tuneInUserSerial: selectTuneInUserSerial(state),
    abTestIds: get(state, ['config', 'autoupdate', 'abtest.ids'], ''),
    mwLandingBypassCookieVal: state.app.mwLandingBypassCookieVal,
  };
}

export default function withMobileJourney(TargetComponent) {
  class WithMobileJourney extends Component {
    static propTypes = {
      actions: PropTypes.object.isRequired,
      routeProps: PropTypes.object.isRequired,
      tuneInUserSerial: PropTypes.string.isRequired,
      isMobile: PropTypes.bool.isRequired,
      abTestIds: PropTypes.string.isRequired,
      mwLandingBypassCookieVal: PropTypes.bool.isRequired,
      [MW_LANDING_LANDINGONLY_DISABLED]: PropTypes.bool.isRequired,
      [MW_LANDING_DIRECT_TO_NOW_PLAYING]: PropTypes.bool.isRequired,
      [MW_LANDING_DISABLED_GUIDEIDS]: PropTypes.string,
      [MW_LANDING_DISABLED]: PropTypes.bool.isRequired,
      [PLAY_OPEN_IN_APP_ENABLED]: PropTypes.bool,
    };

    constructor(props) {
      super(props);

      const {
        actions,
        [MW_LANDING_DIRECT_TO_NOW_PLAYING]: mwLandingDirectToNowPlayingTest,
        routeProps,
        isMobile,
      } = this.props;

      if (isServer()) {
        const guideId = get(routeProps, 'guideContext.guideId');
        if (mwLandingDirectToNowPlayingTest && isMobile && isProfile(guideId)) {
          actions.openNowPlayingDialog(guideId);
        }
      }

      if (!isBranchInitialized()) {
        return;
      }

      this.state = {
        shouldAddListeners: true,
      };

      const matchUrl = get(props, 'routeProps.matchUrl');
      if (!isBlacklistedBranchRoute(matchUrl)) {
        this.openBranchJourney(false);
        this.state.shouldAddListeners = false;
      }
    }

    componentDidUpdate(prevProps) {
      if (isBranchInitialized() && didRouteChange(prevProps, this.props)) {
        const currentRoute = get(this.props, 'routeProps.matchUrl');
        const prevRoute = get(prevProps, 'routeProps.matchUrl');
        const isCurrentRouteBlacklisted =
          isBlacklistedBranchRoute(currentRoute);
        const isPrevRouteBlacklisted = isBlacklistedBranchRoute(prevRoute);

        // if navigating to a whitelisted page from a whitelisted page, restart branch journey
        if (!isCurrentRouteBlacklisted && !isPrevRouteBlacklisted) {
          this.updateBranchJourney(closeBranchJourney, true);

          // if navigating to a whitelisted page from a blacklisted page, open branch journey
        } else if (!isCurrentRouteBlacklisted && isPrevRouteBlacklisted) {
          this.updateBranchJourney(this.openBranchJourney);

          // if navigating to a blacklisted page from a whitelisted page, close branch journey
        } else if (!isPrevRouteBlacklisted) {
          this.updateBranchJourney(closeBranchJourney);
        }
      }
    }

    setBranchUrl(branchUrl) {
      this.props.actions.updateBranchLink(branchUrl);
    }

    updateBranchJourney(action, shouldRestartBranchJourney = false) {
      this.setState({ shouldRestartBranchJourney }, action);
    }

    openBranchJourney(canSetState = true) {
      const {
        tuneInUserSerial,
        abTestIds,
        routeProps,
        [PLAY_OPEN_IN_APP_ENABLED]: playRedirectToAppStoreTest,
      } = this.props;
      const { shouldAddListeners } = this.state;

      const guideId = get(routeProps, 'guideContext.guideId');
      const title = get(routeProps, 'guideContext.title');
      const customBranchCampaign = playRedirectToAppStoreTest
        ? playCampaign
        : undefined;
      const options = {
        guideId,
        title,
        serial: tuneInUserSerial,
        abTestIds,
        customCampaign: customBranchCampaign,
      };

      if (shouldAddListeners) {
        this.addBranchListeners();
      }

      if (shouldAddListeners && canSetState) {
        this.setState({ shouldAddListeners: false });
      }

      setBranchViewData(options);
      reopenBranchJourney();
      createBranchLink(options, this.setBranchUrl.bind(this));
    }

    addBranchListeners() {
      const { actions, routeProps } = this.props;
      addBranchListener('didShowJourney', () =>
        actions.setBranchShowingStatus(true),
      );
      addBranchListener('didCloseJourney', () => {
        actions.setBranchShowingStatus(false);

        if (this.state.shouldRestartBranchJourney) {
          this.updateBranchJourney(this.openBranchJourney);
        }
      });

      const guideId = get(routeProps, 'guideContext.guideId');
      addBranchListener('didClickJourneyCTA', () => {
        gtagTrack.branchInstallClick();
        gotieTrackClick(guideId);
        actions.logMobileWebActivity(
          mobileWeb.actions.tap,
          mobileWeb.labels.branchBannerAppDownload,
        );
      });
    }

    render() {
      let shouldShowMobileOverlay = false;

      const {
        [MW_LANDING_DIRECT_TO_NOW_PLAYING]: mwLandingDirectToNowPlayingTest,
        [MW_LANDING_DISABLED]: mwLandingDisabledTest,
        [MW_LANDING_DISABLED_GUIDEIDS]: mwLandingDisabledGuideIdsTest,
        [MW_LANDING_LANDINGONLY_DISABLED]: mwLandingLandingOnlyEnabledTest,
        mwLandingBypassCookieVal, // mobile overlay turned off by cookie
        isMobile,
        routeProps,

        // destructuring props below to prevent passing to target component
        /* eslint-disable no-unused-vars */
        [PLAY_OPEN_IN_APP_ENABLED]: playRedirectToAppStoreTest,
        tuneInUserSerial,
        abTestIds,
        actions,
        /* eslint-enable no-unused-vars */

        ...other // eslint-disable-line comma-dangle
      } = this.props;

      if (isMobile) {
        const mwLandingDisabledGuideIds = mwLandingDisabledGuideIdsTest
          ? mwLandingDisabledGuideIdsTest.split(',')
          : '';

        const { guideId } = routeProps.guideContext;

        // overlay disabled by cookie or tests
        const disableMWOverlay =
          mwLandingBypassCookieVal || // remembered by cookie
          // mobile overlay turned off
          mwLandingDisabledTest ||
          // mobile overlay turned off for current guideId
          includes(mwLandingDisabledGuideIds, guideId) ||
          // if ⬇️ === true, mobile overlay supressed in lieu of nowPlayingDiaglog
          (isProfile(guideId) && mwLandingDirectToNowPlayingTest) ||
          // enabled ONLY for the homepage
          (routeProps.isLanding && mwLandingLandingOnlyEnabledTest);

        // overlay enabled by
        shouldShowMobileOverlay = !!(
          (
            routeProps.isOverlayablePage && // determined by route
            !disableMWOverlay
          ) // and whenever not disabled
        );
      }

      return (
        <TargetComponent
          {...other}
          routeProps={routeProps}
          shouldShowMobileOverlay={shouldShowMobileOverlay}
        />
      );
    }
  }

  return flow(
    connect(mapStateToProps, mapDispatchToProps),
    connectWithExperiments([
      MW_LANDING_LANDINGONLY_DISABLED,
      MW_LANDING_DIRECT_TO_NOW_PLAYING,
      MW_LANDING_DISABLED,
      MW_LANDING_DISABLED_GUIDEIDS,
      PLAY_OPEN_IN_APP_ENABLED,
    ]),
  )(WithMobileJourney);
}
